<script setup>
import { computed, ref, onMounted } from 'vue';

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
    // Each item: { name, thumbnail, values: [{ label, value, color }] }
  },
  maxValue: {
    type: Number,
    default: 0,
  },
  colorScheme: {
    type: String,
    default: 'green', // 'green' | 'orange'
  },
  legendItems: {
    type: Array,
    default: () => [],
    // Each: { label, color }
  },
});

const animated = ref(false);

onMounted(() => {
  requestAnimationFrame(() => {
    animated.value = true;
  });
});

const computedMax = computed(() => {
  if (props.maxValue > 0) return props.maxValue;
  let max = 0;
  props.items.forEach(item => {
    const total = (item.values || []).reduce((s, v) => s + (v.value || 0), 0);
    if (total > max) max = total;
  });
  return max || 1;
});

const gridInfo = computed(() => {
  const max = computedMax.value;
  let step;
  if (max <= 8) step = 1;
  else if (max <= 16) step = 2;
  else if (max <= 40) step = 4;
  else if (max <= 80) step = 8;
  else step = Math.ceil(max / 8);

  let gridMax = Math.ceil(max / step) * step;
  if (gridMax === max) gridMax += step;
  const steps = gridMax / step;
  return { max: gridMax, step, steps };
});

function getInitials(name) {
  const parts = (name || '').trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return (name || '??').substring(0, 2).toUpperCase();
}

function truncName(name, maxLen = 8) {
  if (!name || name.length <= maxLen) return name;
  return name.substring(0, maxLen - 1).trimEnd() + '\u2026';
}

function totalFor(item) {
  return (item.values || []).reduce((s, v) => s + (v.value || 0), 0);
}

function barWidth(value) {
  if (!animated.value) return '0%';
  const pct = (value / gridInfo.value.max) * 100;
  return pct + '%';
}
</script>

<template>
  <div class="sz-hbar">
    <!-- Bar rows -->
    <div class="sz-hbar-area">
      <div
        v-for="(item, idx) in items"
        :key="idx"
        class="sz-hbar-row"
      >
        <!-- Agent / Team name -->
        <div class="sz-hbar-agent">
          <template v-if="!item.noAvatar">
            <img
              v-if="item.thumbnail"
              :src="item.thumbnail"
              :alt="item.name"
              class="sz-hbar-avatar"
            >
            <div
              v-else
              class="sz-hbar-avatar-txt"
              :style="{ background: (item.values && item.values[0]) ? item.values[0].color : undefined }"
            >
              {{ getInitials(item.name) }}
            </div>
          </template>
          <span
            class="sz-hbar-name"
            :title="item.name"
          >{{ truncName(item.name) }}</span>
        </div>

        <!-- Bar track -->
        <div class="sz-hbar-bar-wrap">
          <div class="sz-hbar-bar-track">
            <!-- Grid lines -->
            <div
              v-for="g in (gridInfo.steps + 1)"
              :key="'g' + g"
              class="sz-hbar-gridline"
              :style="{ left: ((g - 1) / gridInfo.steps) * 100 + '%' }"
            />
            <!-- Stacked bars -->
            <div
              v-for="(seg, si) in (item.values || [])"
              :key="'s' + si"
              class="sz-hbar-bar"
              :style="{
                width: barWidth(seg.value),
                background: seg.color,
                left: barWidth(
                  (item.values || []).slice(0, si).reduce((s, v) => s + (v.value || 0), 0)
                ),
              }"
              :title="seg.label + ': ' + seg.value"
            />
          </div>
          <span class="sz-hbar-val">{{ totalFor(item) }}</span>
        </div>
      </div>
    </div>

    <!-- X-axis labels -->
    <div class="sz-hbar-xaxis">
      <div class="sz-hbar-agent-spacer" />
      <div class="sz-hbar-axis-labels">
        <span
          v-for="x in (gridInfo.steps + 1)"
          :key="'x' + x"
        >{{ (x - 1) * gridInfo.step }}</span>
      </div>
      <div class="sz-hbar-val-spacer" />
    </div>

    <!-- Legend -->
    <div
      v-if="legendItems.length"
      class="sz-hbar-legend"
    >
      <div
        v-for="(leg, li) in legendItems"
        :key="'l' + li"
        class="sz-hbar-legend-item"
      >
        <span
          class="sz-hbar-dot"
          :style="{ background: leg.color }"
        />
        <span>{{ leg.label }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.sz-hbar {
  padding: 4px 0 0;
  width: 100%;
  min-width: 0;
  flex: 1;
}

.sz-hbar-area {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.sz-hbar-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 32px;
}

.sz-hbar-agent {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100px;
  flex-shrink: 0;
}

.sz-hbar-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.sz-hbar-avatar-txt {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #2C7B2C;
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.sz-hbar-name {
  font-size: 12px;
  font-weight: 500;
  color: var(--n-slate-12);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sz-hbar-bar-wrap {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
}

.sz-hbar-bar-track {
  flex: 1;
  height: 20px;
  position: relative;
  border-radius: 4px;
  overflow: hidden;
  background: transparent;
}

.sz-hbar-gridline {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background: rgba(0, 0, 0, 0.06);
  pointer-events: none;
}

.sz-hbar-bar {
  position: absolute;
  top: 0;
  height: 100%;
  border-radius: 4px;
  transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: default;
}

.sz-hbar-bar + .sz-hbar-bar {
  border-left: 2px solid #FFFFFF;
}

.sz-hbar-val {
  font-size: 13px;
  font-weight: 600;
  color: var(--n-slate-12);
  min-width: 28px;
  text-align: right;
  font-variant-numeric: tabular-nums;
}

/* X-axis */
.sz-hbar-xaxis {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
}

.sz-hbar-agent-spacer {
  width: 100px;
  flex-shrink: 0;
}

.sz-hbar-axis-labels {
  flex: 1;
  display: flex;
  justify-content: space-between;
}

.sz-hbar-axis-labels span {
  font-size: 10px;
  color: var(--n-slate-11);
}

.sz-hbar-val-spacer {
  min-width: 28px;
}

/* Legend */
.sz-hbar-legend {
  display: flex;
  gap: 16px;
  margin-top: 12px;
  justify-content: flex-end;
}

.sz-hbar-legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--n-slate-11);
}

.sz-hbar-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

</style>

<style>
/* Dark mode */
.dark .sz-hbar-avatar-txt { background: #2E7D32; }
.dark .sz-hbar-gridline { background: rgba(255,255,255,0.06); }
.dark .sz-hbar-name { color: #D4D4D4; }
.dark .sz-hbar-val { color: #E5E7EB; }
.dark .sz-hbar-legend-item { color: #9CA3AF; }
.dark .sz-hbar-axis-labels span { color: #6B7280; }
.dark .sz-hbar-bar + .sz-hbar-bar { border-left-color: #1F2125; }
</style>
